import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/config';
import { formattaData } from '../utils/dateUtils';
import { pdf } from '@react-pdf/renderer';
import FatturaPDF from '../pdf/FatturaPDF';

function fmtCHF(num) {
  const n = Number(num) || 0;
  return n.toLocaleString('it-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtCHFivaEsclusa(num) {
  return `${fmtCHF(num)} CHF + IVA`;
}

async function apriPdfFattura(fattura) {
  try {
    const [cliSnap, cantSnap] = await Promise.all([
      get(ref(database, `clienti/${fattura.clienteId}`)),
      fattura.cantiereId ? get(ref(database, `cantieri/${fattura.cantiereId}`)) : Promise.resolve(null),
    ]);

    const clienteObj = cliSnap.exists() ? cliSnap.val() : null;
    const cantiereObj = cantSnap && cantSnap.exists() ? cantSnap.val() : null;

    const blob = await pdf(
      <FatturaPDF
        fattura={fattura}
        cliente={clienteObj}
        cantiere={cantiereObj}
      />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (err) {
    console.error('Errore apertura PDF fattura:', err);
    alert('Errore apertura PDF fattura: ' + err.message);
  }
}

export default function ReportFinanziario({ onBack }) {
  const [fatture, setFatture] = useState([]);
  const [clienti, setClienti] = useState({});
  const [cantieri, setCantieri] = useState({});
  const [loading, setLoading] = useState(true);
  const [modo, setModo] = useState(null); // 'cliente' | 'nonPagate'
  const [clienteIdSelezionato, setClienteIdSelezionato] = useState('');
  const [datiCliente, setDatiCliente] = useState(null);

  useEffect(() => {
    async function carica() {
      const [fattureSnap, clientiSnap, cantieriSnap] = await Promise.all([
        get(ref(database, 'fatture')),
        get(ref(database, 'clienti')),
        get(ref(database, 'cantieri')),
      ]);

      if (clientiSnap.exists()) {
        setClienti(clientiSnap.val()); // { id: {ragioneSociale, ...} }
      }

      if (cantieriSnap.exists()) {
        setCantieri(cantieriSnap.val()); // { id: {luogo, ...} }
      }

      if (fattureSnap.exists()) {
        const lista = Object.entries(fattureSnap.val()).map(([id, v]) => ({ id, ...v }));
        setFatture(lista);
      } else {
        setFatture([]);
      }
      setLoading(false);
    }
    carica();
  }, []);

  function selezionaCliente(clienteId) {
    setClienteIdSelezionato(clienteId);
    if (!clienteId) {
      setDatiCliente(null);
      return;
    }
    const fattureCliente = fatture.filter((f) => f.clienteId === clienteId);
    const fatturatoTotale = fattureCliente.reduce((acc, f) => acc + (Number(f.imponibile) || 0), 0);
    const nonPagate = fattureCliente.filter((f) => !f.pagata);
    const totaleInsoluto = nonPagate.reduce((acc, f) => acc + (Number(f.imponibile) || 0), 0);

    setDatiCliente({
      fattureCliente: [...fattureCliente].sort((a, b) => (b.dataFattura || '').localeCompare(a.dataFattura || '')),
      fatturatoTotale,
      nonPagate,
      totaleInsoluto,
    });
  }

  const fattureNonPagateGlobali = fatture
    .filter((f) => !f.pagata)
    .sort((a, b) => (b.dataFattura || '').localeCompare(a.dataFattura || ''));

  const totaleNonPagateGlobali = fattureNonPagateGlobali.reduce(
    (acc, f) => acc + (Number(f.imponibile) || 0),
    0
  );

  return (
    <div className="lista-container">
      <div className="fatturazione-header">
        <button type="button" className="btn-logout" onClick={onBack}>
          Torna indietro
        </button>
      </div>

      <div className="lista-header">
        <h2>📊 Report finanziario</h2>
      </div>

      {loading ? (
        <p className="loading-text">Caricamento...</p>
      ) : (
        <>
          <div className="fatture-preventivi-actions">
            <button
              className="fatture-preventivi-button"
              onClick={() => setModo('cliente')}
            >
              <span className="fatture-preventivi-icon">👤</span>
              <span>Cliente</span>
            </button>

            <button
              className="fatture-preventivi-button"
              onClick={() => setModo('nonPagate')}
            >
              <span className="fatture-preventivi-icon">⚠️</span>
              <span>Fatture non pagate</span>
            </button>
          </div>

          {modo === 'cliente' && (
            <div style={{ marginTop: 24 }}>
              <div className="form-group" style={{ maxWidth: 320 }}>
                <label>Seleziona cliente</label>
                <select
                  value={clienteIdSelezionato}
                  onChange={(e) => selezionaCliente(e.target.value)}
                >
                  <option value="">-- Seleziona --</option>
                  {Object.entries(clienti)
                    .sort((a, b) => (a[1].ragioneSociale || '').localeCompare(b[1].ragioneSociale || ''))
                    .map(([id, c]) => (
                      <option key={id} value={id}>{c.ragioneSociale || id}</option>
                    ))}
                </select>
              </div>

              {datiCliente && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 400, fontWeight: 'bold', fontSize: 18 }}>
                    <span>Fatturato totale</span>
                    <span>{fmtCHFivaEsclusa(datiCliente.fatturatoTotale)}</span>
                  </div>

                  <h3 style={{ marginTop: 24 }}>Fatture non pagate ({datiCliente.nonPagate.length})</h3>
                  <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 400, fontWeight: 'bold', color: '#c00' }}>
                    <span>Totale insoluto</span>
                    <span>{fmtCHFivaEsclusa(datiCliente.totaleInsoluto)}</span>
                  </div>

                  <table className="data-table" style={{ width: '100%', marginTop: 12 }}>
                    <thead>
                      <tr>
                        <th>Numero fattura</th>
                        <th>PDF</th>
                        <th>Data fattura</th>
                        <th>Cantiere</th>
                        <th>Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datiCliente.nonPagate.map((f) => (
                        <tr key={f.id}>
                          <td>
                            <button
                              type="button"
                              onClick={() => apriPdfFattura(f)}
                              title="Apri PDF fattura"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#0066cc',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                padding: 0,
                                font: 'inherit',
                              }}
                            >
                              {f.numeroFattura || '-'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => apriPdfFattura(f)}
                              title="Apri PDF fattura"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <img src="/icon-pdf.svg" alt="PDF" style={{ width: '30px', display: 'block', margin: '0 auto' }} />
                            </button>
                          </td>
                          <td>{formattaData(f.dataFattura)}</td>
                          <td>{cantieri[f.cantiereId]?.luogo || '-'}</td>
                          <td>{fmtCHFivaEsclusa(f.imponibile)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <h3 style={{ marginTop: 24 }}>Elenco completo fatture ({datiCliente.fattureCliente.length})</h3>
                  <table className="data-table" style={{ width: '100%', marginTop: 12 }}>
                    <thead>
                      <tr>
                        <th>Numero fattura</th>
                        <th>PDF</th>
                        <th>Data fattura</th>
                        <th>Cantiere</th>
                        <th>Importo</th>
                        <th>Pagata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datiCliente.fattureCliente.map((f) => (
                        <tr key={f.id}>
                          <td>
                            <button
                              type="button"
                              onClick={() => apriPdfFattura(f)}
                              title="Apri PDF fattura"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#0066cc',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                padding: 0,
                                font: 'inherit',
                              }}
                            >
                              {f.numeroFattura || '-'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => apriPdfFattura(f)}
                              title="Apri PDF fattura"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <img src="/icon-pdf.svg" alt="PDF" style={{ width: '30px', display: 'block', margin: '0 auto' }} />
                            </button>
                          </td>
                          <td>{formattaData(f.dataFattura)}</td>
                          <td>{cantieri[f.cantiereId]?.luogo || '-'}</td>
                          <td>{fmtCHFivaEsclusa(f.imponibile)}</td>
                          <td style={{ textAlign: 'center' }}>{f.pagata ? 'Sì' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {modo === 'nonPagate' && (
            <div style={{ marginTop: 24 }}>
              <h3>Fatture non pagate ({fattureNonPagateGlobali.length})</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 400, fontWeight: 'bold', color: '#c00', marginBottom: 12 }}>
                <span>Totale insoluto</span>
                <span>{fmtCHFivaEsclusa(totaleNonPagateGlobali)}</span>
              </div>
              <table className="data-table" style={{ width: '100%', marginTop: 12 }}>
                                <thead>
                  <tr>
                    <th>Numero fattura</th>
                    <th>PDF</th>
                    <th>Data fattura</th>
                    <th>Cliente</th>
                    <th>Cantiere</th>
                    <th>Importo</th>
                  </tr>
                </thead>
                <tbody>
                {fattureNonPagateGlobali.map((f) => (
                    <tr key={f.id}>
                      <td>
                        <button
                          type="button"
                          onClick={() => apriPdfFattura(f)}
                          title="Apri PDF fattura"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0066cc',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit',
                          }}
                        >
                          {f.numeroFattura || '-'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => apriPdfFattura(f)}
                          title="Apri PDF fattura"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          <img src="/icon-pdf.svg" alt="PDF" style={{ width: '30px', display: 'block', margin: '0 auto' }} />
                        </button>
                      </td>
                      <td>{formattaData(f.dataFattura)}</td>
                      <td>{clienti[f.clienteId]?.ragioneSociale || '-'}</td>
                      <td>{cantieri[f.cantiereId]?.luogo || '-'}</td>
                      <td>{fmtCHFivaEsclusa(f.imponibile)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}